import {
  getCardStatus,
  getUserCards,
  getCreditInformation,
  getUserConfig,
} from "../api/api";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { useCreditStore } from "../stores/creditStore";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { useTransactionsStore } from "../stores/transactionsStore";
import { useRewardsStore } from "../stores/rewardsStore";
import {
  StoreCreditType,
  getAllCreditProviders,
} from "../config/creditRegistry";
import { useXPHistoryStore } from "../stores/xpHistoryStore";
import { useReferralEarningsStore } from "../stores/referralEarningsStore";
import { useHookTransactionsStore } from "../stores/hookTransactionsStore";

class DataFetcher {
  private static instance: DataFetcher;
  private hasFetched = false;

  static getInstance(): DataFetcher {
    if (!DataFetcher.instance) {
      DataFetcher.instance = new DataFetcher();
    }
    return DataFetcher.instance;
  }

  async fetchAllData(): Promise<void> {
    const { jwt, isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized || !jwt?.eoa || this.hasFetched) {
      return;
    }

    console.log("🚀 SDK: Starting data fetch for:", jwt.eoa);

    const cardStore = useCardStatusStore.getState();
    const creditStore = useCreditStore.getState();

    // Set loading states
    cardStore._setLoading(true);
    creditStore._setLoading(true);

    try {
      // Always fetch card status and user cards
      const [cardStatusResponse, userCardsResponse] = await Promise.all([
        getCardStatus(),
        getUserCards(),
      ]);

      const allProviders = getAllCreditProviders();

      // Update card data
      const userCard = userCardsResponse.data?.[0] || null;

      console.log(
        "🔄 Fetching credit info for providers:",
        allProviders.map((p) => p.displayName)
      );

      const userConfig = userCard
        ? {
            solverType: userCard.solverType,
            config: userCard.solverConfig,
          }
        : await getUserConfig();

      creditStore._setUserConfig(userConfig ? userConfig["config"] : null);

      const creditPromises = allProviders.map((provider) => {
        const currentConfig =
          userConfig?.solverType === provider.id
            ? userConfig["config"]
            : undefined;

        return getCreditInformation(
          provider.id,
          provider.chainId,
          currentConfig
        );
      });

      const creditResponses = await Promise.all(creditPromises);
      creditResponses.forEach((response, index) => {
        const provider = allProviders[index];
        creditStore._setCreditInfo(
          provider.storeKey as StoreCreditType,
          response
        );
      });
      cardStore._setCardStatus(cardStatusResponse.data, userCard);

      // Fetch rewards data
      const rewardsStore = useRewardsStore.getState();
      rewardsStore.fetchReferralStats();
      if (jwt.eoa) {
        rewardsStore.fetchMilestoneStatus();
        rewardsStore.fetchXPBalance();
      }
      console.log("SDK: Initiated rewards data fetch (non-blocking)");

      this.hasFetched = true;
      console.log(
        "✅ SDK: Successfully fetched data for configured providers:",
        jwt.eoa
      );
    } catch (error) {
      console.error("❌ SDK: Error fetching data:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch data";

      cardStore._setError(errorMessage);
      creditStore._setError(errorMessage);
    } finally {
      cardStore._setLoading(false);
      creditStore._setLoading(false);
    }
  }

  // Public method to trigger data fetching after authentication
  async fetchDataAfterAuth(): Promise<void> {
    // Reset the fetch flag to allow fetching again
    this.hasFetched = false;
    await this.fetchAllData();
  }

  async refreshConfiguredProviders(): Promise<void> {
    const allProviders = getAllCreditProviders();
    const storeKeys = allProviders.map((p) => p.storeKey as StoreCreditType);
    await useCreditStore.getState()._refreshPositions(storeKeys);
  }

  reset(): void {
    this.hasFetched = false;
    useCardStatusStore.getState()._reset();
    useCreditStore.getState()._reset();
    useTransactionsStore.getState()._reset();
    useHookTransactionsStore.getState()._reset();
    useRewardsStore.getState().reset();
    useXPHistoryStore.getState()._reset();
    useReferralEarningsStore.getState()._reset();
  }
}

export const dataFetcher = DataFetcher.getInstance();
